import { useState, useCallback } from 'react';
import { apiFetch } from '../api';

function GlobalSearch({ refreshKey }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/search?q=${encodeURIComponent(q)}`);
      if (res.ok) setResults(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => doSearch(val), 300));
  }

  function formatCurrency(amount) {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <section className="card gs-section">
      <h2>Search Everything</h2>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        placeholder="Search transactions, accounts, receipts, packages..."
        className="sfin-tx-search"
        style={{ marginBottom: 12 }}
      />

      {loading && <p className="loading" style={{ fontSize: 13 }}>Searching...</p>}

      {results && !loading && (
        <div className="gs-results">
          <div className="gs-result-count">{results.totalCount} result{results.totalCount !== 1 ? 's' : ''}</div>

          {results.transactions.length > 0 && (
            <div className="gs-group">
              <h3>Transactions ({results.transactions.length})</h3>
              {results.transactions.map((tx) => (
                <div key={tx.id} className="gs-item">
                  <div className="gs-item-main">
                    <span className="gs-item-title">{tx.description}</span>
                    <span className={`gs-item-amount ${parseFloat(tx.amount) >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(tx.amount)}
                    </span>
                  </div>
                  <div className="gs-item-meta">
                    <span>{tx.accountName}</span>
                    {tx.category && <span className="sfin-tx-category-badge">{tx.category}</span>}
                    <span>{formatDate(tx.posted)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {results.accounts.length > 0 && (
            <div className="gs-group">
              <h3>Accounts ({results.accounts.length})</h3>
              {results.accounts.map((a) => (
                <div key={a.id} className="gs-item">
                  <div className="gs-item-main">
                    <span className="gs-item-title">{a.name}</span>
                    <span className="gs-item-amount">{formatCurrency(a.balance)}</span>
                  </div>
                  {a.category && <div className="gs-item-meta"><span className="sfin-tx-category-badge">{a.category}</span></div>}
                </div>
              ))}
            </div>
          )}

          {results.receipts.length > 0 && (
            <div className="gs-group">
              <h3>Receipts ({results.receipts.length})</h3>
              {results.receipts.map((r) => (
                <div key={r.id} className="gs-item">
                  <div className="gs-item-main">
                    <span className="gs-item-title">{r.vendor}</span>
                    {r.amount != null && <span className="gs-item-amount negative">{formatCurrency(r.amount)}</span>}
                  </div>
                  <div className="gs-item-meta">
                    {r.category && <span className="sfin-tx-category-badge">{r.category}</span>}
                    <span>{r.date}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {results.packages.length > 0 && (
            <div className="gs-group">
              <h3>Packages ({results.packages.length})</h3>
              {results.packages.map((p) => (
                <div key={p.id} className="gs-item">
                  <div className="gs-item-main">
                    <span className="gs-item-title">{p.description || p.carrier}</span>
                    <span className="gs-item-status">{p.status}</span>
                  </div>
                  <div className="gs-item-meta">
                    <span>{p.carrier}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.trackingNumber}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {results.totalCount === 0 && (
            <p style={{ color: '#71717a', fontSize: 13, textAlign: 'center', padding: 20 }}>
              No results found for "{query}"
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export default GlobalSearch;
